import React from 'react';
import { Link } from 'react-router-dom';
import { useSiteContent } from '../context/SiteContentContext';
import { useConnection } from '../context/ConnectionContext';
import useAnnouncements from '../hooks/useAnnouncements';
import StoreBadges from './StoreBadges';
import SocialLinks from './SocialLinks';
import countryPages from '../content/countryPages.json';
import { COUNTRY_PAGE_PREFIX } from '../utils/whatsapp';
import './Footer.css';
import defaultLogo from '../new_logo-removebg-preview.png';

const Footer = () => {
  const { globalContent } = useSiteContent();
  const { isLite, manualPreference, detectedSlow, setManualLite } = useConnection();

  const logo        = globalContent.footer_logo_url || defaultLogo;
  const companyName = globalContent.company_name    || 'VisionExploit';
  const companyTagline = globalContent.company_tagline
    || 'Your trusted partner in achieving international education dreams. Expert guidance, personalized support, proven success.';
  // .trim() guards against a CMS value that is whitespace-only rather than
  // empty/undefined — `||` alone would treat " " as truthy and still render
  // an empty, focusable <a> (the link-name violation this fixes).
  const email      = (globalContent.email       || 'visionexploitconsultants@gmail.com').trim();
  const phone1     = (globalContent.phone_1     || '+90 552 811 7433').trim();
  const phone2     = (globalContent.phone_2     || '').trim();
  const websiteUrl = (globalContent.website_url || 'https://www.visionexploit.com').trim();
  const websiteDisplay = websiteUrl.replace(/^https?:\/\//, '');
  const address = (globalContent.address || '').trim();


  // Quick links: CMS JSON takes priority; fall back to defaults
  const DEFAULT_QUICK_LINKS = [
    { label: 'About Us',     href: '/about' },
    { label: 'Services',     href: '/services' },
    { label: 'Universities', href: '/universities' },
    { label: 'Programs',     href: '/programs' },
    { label: 'Pricing',      href: '/pricing' },
    { label: 'Sign Up',      href: '/sign-up' },
  ];
  let quickLinks = DEFAULT_QUICK_LINKS;
  if (globalContent.quick_links) {
    try {
      const parsed = JSON.parse(globalContent.quick_links);
      if (Array.isArray(parsed) && parsed.length) quickLinks = parsed;
    } catch (_) {}
  }

  const services = [
    { name: 'University Selection', href: '/universities' },
    { name: 'Visa Assistance', href: '/services' },
    { name: 'Career Counseling', href: '/services' },
    { name: 'Accommodation', href: '/services' },
    { name: 'Study Destinations', href: '/destinations' },
  ];

  const { items: latestAnnouncements } = useAnnouncements({ limit: 3 });

  // App store badges — the Student App is on Google Play (and the App Store
  // once the iOS build ships). URLs are CMS-editable at General Settings →
  // Mobile app; the Play URL falls back to the known package id, and each
  // badge only renders when its URL is set.
  const playStoreUrl = (globalContent.app_play_store_url || '').trim();
  const appStoreUrl  = (globalContent.app_app_store_url  || '').trim();

  return (
    <footer className="footer" id="site-footer">
      <div className="footer-container">
        <div className="footer-grid">
          {/* Company Info */}
          <div className="footer-section">
            <Link to="/" className="footer-logo">
              <img
                src={logo}
                alt={`${companyName} logo`}
                className="footer-logo-img"
                width={32}
                height={32}
                loading="lazy"
                decoding="async"
                onError={(e) => {
                  // A dead CMS-supplied URL (broken upload host, expired
                  // link) previously rendered as a broken-image icon in the
                  // footer on every page. Null the handler before swapping
                  // so a bad bundled fallback couldn't loop.
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = defaultLogo;
                }}
              />
              <span className="company-name">{companyName}</span>
            </Link>
            <p className="company-description">{companyTagline}</p>
            <div className="contact-info">
              {email && (
                <p>
                  📧 <a href={`mailto:${email}`} className="contact-link">{email}</a>
                </p>
              )}
              {phone1 && (
                <p>
                  📞 <a href={`tel:${phone1.replace(/\s/g, '')}`} className="contact-link">{phone1}</a>
                </p>
              )}
              {phone2 && (
                <p>
                  📞 <a href={`tel:${phone2.replace(/\s/g, '')}`} className="contact-link">{phone2}</a>
                </p>
              )}
              {websiteUrl && (
                <p>
                  🌐 <a href={websiteUrl} target="_blank" rel="noopener noreferrer" className="contact-link">{websiteDisplay}</a>
                </p>
              )}
              {address && (
                <p className="footer-address">📍 {address}</p>
              )}
            </div>
            <SocialLinks listClassName="footer-social-links" linkClassName="footer-social-link" label="Follow us on social media" />
          </div>

          {/* Quick Links — driven by CMS (General Settings → Footer Quick Links) */}
          <div className="footer-section">
            <p className="footer-heading">Quick Links</p>
            <ul className="footer-links" aria-label="Quick Links">
              {quickLinks.map((link, idx) => {
                const label = (link.label || link.name || '').trim();
                const href  = link.href  || link.url  || '/';
                if (!label) return null;
                const isExternal = href.startsWith('http://') || href.startsWith('https://');
                return (
                  <li key={idx}>
                    {isExternal ? (
                      <a href={href} className="footer-link" target="_blank" rel="noopener noreferrer">
                        {label}
                      </a>
                    ) : (
                      <Link to={href} className="footer-link">{label}</Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Services */}
          <div className="footer-section">
            <p className="footer-heading">Our Services</p>
            <ul className="footer-links" aria-label="Our Services">
              {services.map((service) => (
                <li key={service.name}>
                  <Link to={service.href} className="footer-link">
                    {service.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Study in Turkey from… — one link per country page. */}
          <div className="footer-section">
            <p className="footer-heading">Study in Turkey from</p>
            <ul className="footer-links" aria-label="Study in Turkey from your country">
              {countryPages.countries.map((c) => (
                <li key={c.slug}>
                  <Link to={`${COUNTRY_PAGE_PREFIX}${c.slug}`} className="footer-link">{c.name}</Link>
                </li>
              ))}
              <li>
                <Link to="/become-an-agent" className="footer-link">Become an agent</Link>
              </li>
            </ul>
          </div>

          {/* Latest Announcements — renders the section but simply lists no
              items when there are none live, rather than hiding the whole
              column (unlike the homepage section, a footer column disappearing
              entirely would shift every column after it). */}
          <div className="footer-section">
            <p className="footer-heading">Latest Announcements</p>
            {latestAnnouncements.length > 0 ? (
              <ul className="footer-links" aria-label="Latest Announcements">
                {latestAnnouncements.map((a) => (
                  <li key={a.id}>
                    <Link to={`/announcements/${a.slug}`} className="footer-link">
                      {a.title}
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="footer-no-announcements">Nothing new right now — check back soon.</p>
            )}
          </div>
        </div>

        <div className="footer-bottom">
          <div className="footer-bottom-content">
            <p className="copyright">
              © {new Date().getFullYear()} {companyName}. All rights reserved.
            </p>
            <label className="lite-mode-toggle">
              <input
                type="checkbox"
                checked={isLite}
                onChange={(e) => setManualLite(e.target.checked)}
              />
              <span>
                Lite mode (less data)
                {manualPreference === null && detectedSlow && ' — on, detected automatically'}
              </span>
            </label>
            <StoreBadges
              className="footer-store-badges"
              playStoreUrl={playStoreUrl}
              appStoreUrl={appStoreUrl}
            />
            <div className="footer-legal-links">
              <a href="/privacy" className="legal-link">
                Privacy Policy
              </a>
              <a href="/terms" className="legal-link">
                Terms of Service
              </a>
              <a href="/sitemap.xml" className="legal-link">
                Sitemap
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;